<template>
  <div class="bcsv">
    <router-link
      class="bcsv-back"
      :to="{ name: 'BatchTransfer' }">
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6">
        <path d="M7.5 2 3.5 6l4 4" />
      </svg>
      กลับไปที่ Batch List
    </router-link>

    <template v-if="batch">
      <div class="bcsv-detail-head">
        <div>
          <div class="bcsv-detail-title">
            <h1>{{ batch.fileName }}</h1>
            <span
              class="bcsv-chip"
              :style="uploadChipStyle(batch.uploadStatus)">
              {{ uploadStatusLabel(batch.uploadStatus) }}
            </span>
            <span
              v-if="!isWithdrawNoteStatusPlain(batch.withdrawNoteStatus)"
              class="bcsv-chip"
              :style="withdrawNoteChipStyle(batch.withdrawNoteStatus)">
              Withdraw Note: {{ withdrawNoteStatusLabel(batch.withdrawNoteStatus) }}
            </span>
          </div>

          <div class="bcsv-meta">
            <span>Batch {{ batch.code }}</span>
            <span class="bcsv-meta-dot">·</span>
            <template v-if="batch.fromWarehouse">
              <span>สาขาต้นทาง : {{ batch.fromWarehouse.name }}</span>
              <span class="bcsv-meta-dot">·</span>
            </template>
            <span v-if="batch.createdBy">อัปโหลดโดย : {{ batch.createdBy.email }}</span>
            <span class="bcsv-meta-dot">·</span>
            <span>{{ batch.uploadedAt | dateTimeUserFormat }}</span>
            <span class="bcsv-meta-dot">·</span>
            <span
              class="d-inline-flex align-center"
              style="gap: 6px;">
              <svg
                width="13"
                height="13"
                viewBox="0 0 14 14"
                fill="none"
                stroke="#9AA3AE"
                stroke-width="1.4">
                <rect
                  x="2"
                  y="3"
                  width="10"
                  height="9"
                  rx="1.5" />
                <path d="M2 5.6h10M4.6 3V1.8M9.4 3V1.8" />
              </svg>
              Due date:
              <strong
                v-if="batch.dueDate"
                style="color: #1F2A37;">
                {{ $dayjs(batch.dueDate).format('D MMM YYYY') }}
              </strong>
              <strong
                v-else
                style="color: #9AA3AE; font-weight: 400;">ไม่ระบุ</strong>
            </span>
          </div>
        </div>

        <div
          class="d-flex flex-wrap"
          style="gap: 12px;">
          <button
            type="button"
            class="bcsv-btn bcsv-btn--ghost"
            :disabled="downloadingFile"
            @click="downloadOriginalFile">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5">
              <path d="M7 2v7M4 6.5 7 9.5l3-3M2 12h10" />
            </svg>
            ดาวน์โหลดไฟล์ต้นฉบับ
          </button>
          <button
            v-if="batch.canBeCanceled"
            type="button"
            class="bcsv-btn bcsv-btn--danger"
            @click="confirmCancel">
            ยกเลิก Withdraw Note ทั้งไฟล์
          </button>
        </div>
      </div>

      <div class="bcsv-stats">
        <div
          v-for="tile in statTiles"
          :key="tile.label"
          class="bcsv-stat">
          <div class="bcsv-stat-label">
            {{ tile.label }}
          </div>
          <div
            class="bcsv-stat-value"
            :style="tile.style">
            {{ tile.value }}
          </div>
        </div>
      </div>

      <div
        v-if="batch.uploadStatus === 'failed'"
        class="bcsv-errbox">
        <div class="bcsv-errbox-head">
          <span class="bcsv-errdot" />
          <strong style="font-weight: 600;">Error log</strong>
          <span style="color: #C0392B; font-size: 13px;">
            {{ errorSummary }}
          </span>
          <v-spacer />
          <batch-transfer-copy-prompt-button
            :prompt="fixPrompt"
            tone="danger"
            label="Copy prompt สำหรับ AI" />
        </div>
        <div
          v-for="(err, index) in batchErrors"
          :key="index"
          class="bcsv-errrow">
          <div
            class="bcsv-mono"
            style="color: #9AA3AE;">
            แถว {{ err.row }}
          </div>
          <div
            class="bcsv-mono"
            style="color: #374151;">
            {{ err.value }}
          </div>
          <div style="color: #C0392B;">
            {{ err.message }}
          </div>
        </div>
      </div>

      <div class="bcsv-section-head">
        <h2>ตารางแถวจากไฟล์</h2>
        <span style="color: #6B7280; font-size: 13px;">แยกตาราง 1 กลุ่ม (group) ต่อ 1 ตาราง</span>
      </div>

      <div
        v-if="rowsLoading"
        class="bcsv-placeholder">
        กำลังโหลดข้อมูลแถว…
      </div>
      <div
        v-else
        class="d-grid"
        style="display: grid; gap: 16px;">
        <batch-transfer-group-table
          v-for="group in groupedTables"
          :key="group.key"
          :group-name="group.displayName"
          :reference-code="group.ref"
          :rows="group.rows"
          :failed="batch.uploadStatus === 'failed'"
          @download="downloadGroupCsv(group)" />
      </div>
    </template>

    <div
      v-else-if="loading"
      class="bcsv-placeholder">
      กำลังโหลดข้อมูล…
    </div>
    <div
      v-else
      class="bcsv-placeholder">
      <div style="font-size: 15px; font-weight: 600; color: #1F2A37;">
        เปิดข้อมูล batch นี้ไม่ได้
      </div>
      <div style="font-size: 13px; margin-top: 6px;">
        {{ loadError || 'ไม่พบข้อมูล หรือไม่มีสิทธิ์เข้าถึง' }}
      </div>
      <button
        type="button"
        class="bcsv-btn bcsv-btn--ghost"
        style="margin-top: 16px;"
        @click="fetchDetail">
        ลองใหม่อีกครั้ง
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BatchTransferProvider from '@/resources/BatchTransferProvider'
import BatchTransferGroupTable from '../components/BatchTransferGroupTable.vue'
import BatchTransferCopyPromptButton from '../components/BatchTransferCopyPromptButton.vue'
import {
  uploadStatusLabel,
  uploadChipStyle,
  withdrawNoteStatusLabel,
  withdrawNoteChipStyle,
  isWithdrawNoteStatusPlain
} from '@/assets/js/BatchTransferStatusUtils'
import { buildFixPrompt } from '@/assets/js/BatchTransferPrompts'

const batchTransferProvider = new BatchTransferProvider()

export default {
  name: 'BatchTransferDetail',
  components: {
    BatchTransferGroupTable,
    BatchTransferCopyPromptButton
  },
  data () {
    return {
      batch: null,
      rows: [],
      loading: false,
      loadError: '',
      rowsLoading: false,
      downloadingFile: false
    }
  },
  computed: {
    batchErrors () {
      return (this.batch && this.batch.errors) || []
    },
    errorSummary () {
      return `${this.batchErrors.length} แถวไม่ผ่านการตรวจสอบ — ไม่มี Withdraw Note ถูกสร้าง`
    },
    statTiles () {
      const summary = (this.batch && this.batch.summary) || {}
      return [
        { label: 'Rows', value: summary.rowCount || 0, style: {} },
        { label: 'SKU', value: summary.skuCount || 0, style: {} },
        { label: 'Total qty', value: summary.qty || 0, style: {} },
        { label: 'Withdraw Note created', value: summary.withdrawNoteCount || 0, style: { color: '#12536B' } },
        { label: 'Error rows', value: summary.errorCount || 0, style: { color: '#C0392B' } }
      ]
    },
    fixPrompt () {
      if (!this.batch) return ''
      return buildFixPrompt({
        fileName: this.batch.fileName,
        batchCode: this.batch.code,
        fromWarehouseName: this.batch.fromWarehouse && this.batch.fromWarehouse.name,
        errors: this.batchErrors
      })
    },
    groupedTables () {
      if (!this.batch) return []

      const backendGroups = this.batch.groups || []
      if (backendGroups.length) {
        return backendGroups.map((g) => ({
          key: g.group || 'ungrouped',
          rawGroup: g.group,
          displayName: g.group || 'ungrouped',
          ref: g.ref,
          rows: this.rows.filter((row) => (row.group || '') === (g.group || ''))
        }))
      }

      // batch ที่ failed ไม่มี group จาก backend — จัดกลุ่มเองตามลำดับที่ group โผล่ครั้งแรก
      const order = []
      const map = {}
      this.rows.forEach((row) => {
        const key = row.group || ''
        if (!map[key]) {
          map[key] = []
          order.push(key)
        }
        map[key].push(row)
      })

      return order.map((key) => ({
        key: key || 'ungrouped',
        rawGroup: key,
        displayName: key || 'ungrouped',
        ref: '',
        rows: map[key]
      }))
    }
  },
  mounted () {
    this.fetchDetail()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    uploadStatusLabel,
    uploadChipStyle,
    withdrawNoteStatusLabel,
    withdrawNoteChipStyle,
    isWithdrawNoteStatusPlain,
    readError (error) {
      const message = error && error.message
      if (Array.isArray(message)) return message.join(' · ')
      return message || ''
    },
    async fetchDetail () {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await batchTransferProvider.getById(this.$route.params.id)
        this.batch = data
        await this.fetchAllRows()
      } catch (error) {
        this.batch = null
        this.loadError = this.readError(error)
      }
      this.loading = false
    },
    async fetchAllRows () {
      this.rowsLoading = true
      this.rows = []
      try {
        let page = 1
        let pages = 1
        do {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await batchTransferProvider.getRows(this.batch.id, { page, limit: 500 })
          this.rows = this.rows.concat(data.results || [])
          pages = data.pages || 1
          page += 1
        } while (page <= pages)
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.readError(error) || 'โหลดข้อมูลแถวไม่สำเร็จ',
          type: 'error'
        })
      }
      this.rowsLoading = false
    },
    confirmCancel () {
      this.setModal({
        value: true,
        title: 'ยกเลิก Withdraw Note ทั้งไฟล์',
        message: `ต้องการยกเลิกไฟล์ "${this.batch.fileName}" ทั้งหมด ${this.batch.summary.withdrawNoteCount} `
          + 'Withdraw Note ใช่หรือไม่? การกระทำนี้ย้อนกลับไม่ได้',
        confirmText: 'ยืนยันยกเลิก',
        confirmType: 'error',
        cancelText: 'ยกเลิก',
        cancelType: '',
        onConfirm: () => this.cancelBatch()
      })
    },
    async cancelBatch () {
      try {
        await batchTransferProvider.cancel(this.batch.id)
        this.setSnackbar({
          value: true,
          message: 'ยกเลิก Withdraw Note ทั้งไฟล์เรียบร้อย',
          type: 'success',
          timeout: 3200
        })
        await this.fetchDetail()
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.readError(error) || 'ยกเลิกไม่สำเร็จ',
          type: 'error'
        })
      }
    },
    async downloadOriginalFile () {
      window.open(this.batch.fileUrl, '_blank')
    },
    async downloadGroupCsv (group) {
      try {
        const blob = await batchTransferProvider.exportGroup(this.batch.id, group.rawGroup)
        this.openBlobInNewTab(blob)
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.readError(error) || 'ดาวน์โหลดไม่สำเร็จ',
          type: 'error'
        })
      }
    },
    openBlobInNewTab (blob) {
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }
  }
}
</script>

<style src="@/assets/css/batch-transfer.css"></style>
